// External Imports
import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { getFormValues, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';

// PPUI Imports
import { Container } from '@paypalcorp/pp-react-grid';

// Internal Imports
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { validate } from '../../lib/clientValidatorV3';
import AmountSetup from './AmountSetup';
import StepHeader from './components/StepHeader';
import { isDonateButton, isHostedFlow } from '../../utilsV3/buttonFactoryUtils';
import MailingAddressSwitch from '../../shared/switchPanels/MailingAddressSwitch';
import Note from './components/Note';
import AdvancedUrls from '../../shared/AdvancedUrls';
import AdvancedVariables from './components/AdvancedVariables';
import RecurringSwitch from '../switchPanels/RecurringSwitch';
import RecurringFrequencyOptions from '../switchPanels/RecurringFrequencyOptions';
import CoverFeesSwitch from '../switchPanels/CoverFeesSwitch';
import ProgramsV3 from '../programs/ProgramsV3';
import WizardNavButtons from '../../shared/navigationButtons/WizardNavButtons';
import DraftErrorWarning from '../../shared/alerts/DraftErrorWarning';
import { FPTI_SPEC } from '../../utilsV3/fptiConstants';
import { trackEventWithData } from '../../utilsV3/analytics';
import _isEmpty from 'lodash/isEmpty';

const mapStateToProps = (state) => {
  const { application = {}, buttonFactoryV3 = {} } = state;

  return {
    application,
    formValues: getFormValues(BTN_FACTORY_V3_FORM)(state),
    initialValues: buttonFactoryV3?.formattedButton ?? {},
    steps: {
      currentStep: buttonFactoryV3?.currentStep,
      totalSteps: buttonFactoryV3?.totalSteps,
    },
  };
};

const WizardStep3 = ({
  handleBack,
  handleSubmit,
  application,
  formValues,
  change,
  steps,
  isDraft,
  handleSaveDraft,
  draftInfo,
  showDraftError,
  setHandleQuitForm,
  handleQuitDraftSave,
}) => {
  const userConsentData = application?.userConsentData;
  const trackPageLoadImpression = () => {
    if (isDonateButton()) {
      trackEventWithData(FPTI_SPEC.SET_AMOUNT_PAGE_IM, { step: 3 });
    } else {
      trackEventWithData(
        FPTI_SPEC.ADVANCED_SETTINGS_PAGE_IM,
        userConsentData?.consentNeeded
          ? {
              step: 3,
              consentNeeded: userConsentData?.consentNeeded,
              userAgreementUrl: userConsentData?.userAgreementUrl,
              policyVersion: userConsentData?.policyVersion,
            }
          : { step: 3 }
      );
    }
  };

  useEffect(() => {
    trackPageLoadImpression();
    setHandleQuitForm?.(() => handleSubmit(handleQuitDraftSave));
  }, []);

  const draftBusy = useSelector(
    (state) => state?.buttonFactoryV3?.draftBusy || false
  );

  if (_isEmpty(formValues)) {
    return null;
  }

  return (
    <>
      <Container className="wizard-step-container">
        {showDraftError && <DraftErrorWarning />}
        <StepHeader />

        <form onSubmit={handleSubmit} className="wizard-form">
          {isDonateButton() ? (
            <>
              <AmountSetup
                application={application}
                formValues={formValues}
                change={change}
              />
              <RecurringSwitch />
              {formValues.recurringEnabled === 'yes' && isHostedFlow() && (
                <RecurringFrequencyOptions />
              )}
              {isHostedFlow() ? (
                <>
                  <CoverFeesSwitch
                    showBadge={application.showNewFeatureBadge ?? false}
                  />
                  <ProgramsV3 />
                </>
              ) : null}
            </>
          ) : (
            <>
              <MailingAddressSwitch
                isChecked={formValues.noShipping === 'no'}
              />
              <Note
                isChecked={formValues.noNote === 'no'}
                showBadge={application.showNewFeatureBadge ?? false}
              />
              <AdvancedUrls noCancelReturn={formValues.noCancelReturn} />
              <AdvancedVariables
                extraVariablesView={formValues.extraVariablesView}
                noAdvancedVariables={formValues.noAdvancedVariables}
              />
            </>
          )}
        </form>
      </Container>
      <WizardNavButtons
        handleBack={handleBack}
        handleSubmit={handleSubmit}
        steps={steps}
        isButton={true}
        isDraft={isDraft}
        handleSaveDraft={handleSubmit(handleSaveDraft)}
        draftInfo={draftInfo}
        showDraftSpinner={draftBusy}
      />
    </>
  );
};

WizardStep3.propTypes = {
  buttonFactoryV3: PropTypes.object,
  handleBack: PropTypes.func,
  handleSubmit: PropTypes.func,
  application: PropTypes.object,
  formValues: PropTypes.object,
  change: PropTypes.func,
  onSubmit: PropTypes.func,
  steps: PropTypes.object,
  isDraft: PropTypes.bool,
  handleSaveDraft: PropTypes.func,
  draftInfo: PropTypes.string,
  showDraftButton: PropTypes.bool,
  showDraftError: PropTypes.bool,
};

let WizardStep3Form = reduxForm({
  form: BTN_FACTORY_V3_FORM,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
})(WizardStep3);

WizardStep3Form = connect(mapStateToProps)(WizardStep3Form);

export default WizardStep3Form;
